import './favorite-item-modal.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { DialogCreators } from '../../../utils/dialog-creators';
import { IDataItem } from '../../../models/data.interface';
import { Modal } from '../modal';
import { Util } from '../../../utils/util';
import { ContentType } from "../../../types/enums";
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { Context } from '../../../models/context';
import { DataLayerService } from '../../../services/data-layer-service';
import { LocalizationService } from '../../../services/localization-service';

/**
 * Represents the modal for adding a favorite menu item.
 */
export class FavoriteItemModal extends Modal {
    private readonly _menuItem: IDataItem;
    private readonly _item: { input: string };
    private _bindingHelper: TwoWayBindingHelper<{ input: string }>;

    /**
     * Initializes a new instance of FavoriteItemModal.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {IDataItem} menuItem - The menu item to be added as a favorite.
     */
    constructor(eventBus: EventEmitter, context: Context, dataLayer: DataLayerService, localizationService: LocalizationService, menuItem: IDataItem) {
        super("modal_favorite", eventBus, context, dataLayer, localizationService, { ariaLabel: "Favorite Item" });
        this._menuItem = menuItem;
        this._item = { input: "" };
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setTwoWayListeners();
        this._setEventListeners();
    }

    /**
     * Initializes two-way data binding for the favorite item name input.
     */
    private _setTwoWayListeners(): void {
        this._bindingHelper.bindToInput("input", "txt_favorite_item_name");
    }

    /**
     * Initializes the modal, setting placeholders and event listeners.
     */
    override init(): void {
        super.init();
        this._item.input = "";
        this._setPlaceholder();
        document.getElementById("modal_favorite")?.classList.add("mobile-center");
    }

    /**
     * Attaches event listeners for the modal buttons.
     */
    protected _setEventListeners(): void {
        Util.setElement("onclick", "btn_favorite_apply", this._validateFavoriteItemName);
        Util.setElement("onclick", "btn_favorite_cancel", this.close);
    }

    /**
     * Sets the placeholder text for the favorite item input field.
     */
    private _setPlaceholder(): void {
        const input = document.getElementById("txt_favorite_item_name") as HTMLInputElement | null;
        if (!input || !this._menuItem?.Names?.length)
            return;

        const name = this._localizationService.GetName(this._menuItem.Names);
        setTimeout(() => { this._item.input = name; }, 100);
        input.setAttribute("placeholder", name);
    }

    /**
    * Validates the entered favorite item name before submission.
    */
    private _validateFavoriteItemName = (): void => {
        const name = this._item.input;
        if (!name) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("FavoriteItemNameRequiredError"),
                true
            );
            return this._toggleInputValidation(false);
        }

        if (name.length > 100) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("FavoriteItemNameLengthError"),
                true
            );
            return this._toggleInputValidation(false);
        }

        this._toggleInputValidation(true);
        this._eventBus.emit(EventTypes.FAV_ITEM_ADD, name);
        this.close();

        if (this._dataLayer) {
            const originalItemName = this._localizationService.GetName(this._menuItem.Names);
            this._dataLayer.favorite(ContentType.MENU_ITEM, originalItemName);
        }
    }

    /**
     * Toggles the invalid input styling based on validation status.
     * @param {boolean} isValid - Whether the input is valid.
     */
    private _toggleInputValidation(isValid: boolean): void {
        const input = document.getElementById("txt_favorite_item_name");
        if (!input)
            return;

        input.classList.toggle("invalid-input", !isValid);
    }

    override close = (): void => {
        this._bindingHelper?.clearListeners();
        Util.setElement("onclick", "btn_favorite_apply", null);
        Util.setElement("onclick", "btn_favorite_cancel", null);
        super.close();
    }
}